import JSZip from 'jszip'
import localStorageService from './localStorageService'

class BackupService {
  constructor() {
    this.dbName = 'NotesAppLocal'
    this.version = 1
  }

  /**
   * Create a backup of all notes, folders, and attachments
   * @returns {Promise<Blob>} ZIP file blob
   */
  async createBackup() {
    try {
      console.log('🔄 BackupService: Starting backup process...')
      
      // Get all data from IndexedDB
      const [notes, folders, attachments] = await Promise.all([
        localStorageService.getAllNotes(),
        localStorageService.getAllFolders(),
        localStorageService.getAllAttachments()
      ])

      console.log('📊 BackupService: Data collected:', {
        notes: notes.length,
        folders: folders.length,
        attachments: attachments.length
      })

      // Create ZIP file
      const zip = new JSZip()
      
      // Add backup metadata
      const metadata = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        appName: 'NoteSpark',
        totalNotes: notes.length,
        totalFolders: folders.length,
        totalAttachments: attachments.length
      }
      
      zip.file('metadata.json', JSON.stringify(metadata, null, 2))
      
      // Add notes
      if (notes.length > 0) {
        zip.file('notes.json', JSON.stringify(notes, null, 2))
        console.log(`📝 BackupService: Added ${notes.length} notes to backup`)
      }
      
      // Add folders
      if (folders.length > 0) {
        zip.file('folders.json', JSON.stringify(folders, null, 2))
        console.log(`📁 BackupService: Added ${folders.length} folders to backup`)
      }
      
      // Add attachments with their data
      if (attachments.length > 0) {
        const attachmentFolder = zip.folder('attachments')
        const attachmentIndex = []
        
        for (const attachment of attachments) {
          if (attachment.data) {
            // Convert base64 to ArrayBuffer for ZIP storage
            const binaryString = atob(attachment.data)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }
            
            // Store the actual file data as ArrayBuffer
            attachmentFolder.file(attachment.fileName, bytes.buffer)
            
            // Store metadata without the large data blob (using local storage format)
            attachmentIndex.push({
              id: attachment.id,
              fileName: attachment.fileName,
              fileType: attachment.fileType,
              fileSize: attachment.fileSize,
              uploadedAt: attachment.uploadedAt
            })
          }
        }
        
        zip.file('attachments.json', JSON.stringify(attachmentIndex, null, 2))
        console.log(`📎 BackupService: Added ${attachments.length} attachments to backup`)
      }

      // Generate ZIP blob
      console.log('🗜️ BackupService: Compressing data...')
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      })
      
      console.log('✅ BackupService: Backup created successfully', {
        size: `${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`,
        compressed: true
      })
      
      return zipBlob
    } catch (error) {
      console.error('❌ BackupService: Failed to create backup:', error)
      throw new Error(`Backup failed: ${error.message}`)
    }
  }

  /**
   * Restore data from a backup ZIP file
   * @param {File} zipFile - The backup ZIP file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Restore results
   */
  async restoreBackup(zipFile, onProgress = () => {}) {
    try {
      console.log('🔄 BackupService: Starting restore process...')
      onProgress(10, 'Reading backup file...')
      
      // Read ZIP file
      const zip = new JSZip()
      const zipContent = await zip.loadAsync(zipFile)
      
      onProgress(20, 'Extracting backup data...')
      
      // Extract metadata
      let metadata = null
      if (zipContent.files['metadata.json']) {
        const metadataContent = await zipContent.files['metadata.json'].async('string')
        metadata = JSON.parse(metadataContent)
        console.log('📋 BackupService: Backup metadata:', metadata)
      }
      
      // Extract notes
      let backupNotes = []
      if (zipContent.files['notes.json']) {
        const notesContent = await zipContent.files['notes.json'].async('string')
        backupNotes = JSON.parse(notesContent)
        console.log(`📝 BackupService: Found ${backupNotes.length} notes in backup`)
      }
      
      onProgress(40, 'Processing notes...')
      
      // Extract folders
      let backupFolders = []
      if (zipContent.files['folders.json']) {
        const foldersContent = await zipContent.files['folders.json'].async('string')
        backupFolders = JSON.parse(foldersContent)
        console.log(`📁 BackupService: Found ${backupFolders.length} folders in backup`)
      }
      
      onProgress(60, 'Processing folders...')
      
      // Extract attachments
      let backupAttachments = []
      let attachmentIndex = []
      if (zipContent.files['attachments.json']) {
        const attachmentsContent = await zipContent.files['attachments.json'].async('string')
        attachmentIndex = JSON.parse(attachmentsContent)
        
        // Reconstruct attachments with their data
        for (const attachmentMeta of attachmentIndex) {
          const attachmentPath = `attachments/${attachmentMeta.fileName}`
          if (zipContent.files[attachmentPath]) {
            const attachmentData = await zipContent.files[attachmentPath].async('arraybuffer')
            
            // Convert ArrayBuffer back to base64 for local storage
            const bytes = new Uint8Array(attachmentData)
            const chunkSize = 8192
            let binaryString = ''
            
            for (let i = 0; i < bytes.length; i += chunkSize) {
              const chunk = bytes.slice(i, i + chunkSize)
              binaryString += String.fromCharCode.apply(null, chunk)
            }
            
            const base64Data = btoa(binaryString)
            
            backupAttachments.push({
              ...attachmentMeta,
              data: base64Data
            })
          }
        }
        console.log(`📎 BackupService: Found ${backupAttachments.length} attachments in backup`)
      }
      
      onProgress(80, 'Merging with existing data...')
      
      // Get existing data
      const [existingNotes, existingFolders, existingAttachments] = await Promise.all([
        localStorageService.getAllNotes(),
        localStorageService.getAllFolders(),
        localStorageService.getAllAttachments()
      ])
      
      // Merge data (existing data wins in conflicts)
      const mergeResults = await this.mergeData({
        notes: { existing: existingNotes, backup: backupNotes },
        folders: { existing: existingFolders, backup: backupFolders },
        attachments: { existing: existingAttachments, backup: backupAttachments }
      })
      
      onProgress(100, 'Restore completed!')
      
      console.log('✅ BackupService: Restore completed successfully')
      return {
        success: true,
        metadata,
        mergeResults
      }
    } catch (error) {
      console.error('❌ BackupService: Failed to restore backup:', error)
      throw new Error(`Restore failed: ${error.message}`)
    }
  }

  /**
   * Clean up duplicate folders in existing data
   */
  async cleanupDuplicateFolders(existingFolders) {
    const foldersToKeep = []
    const seenTypes = new Set()
    const seenNames = new Set()
    let duplicatesRemoved = 0
    
    for (const folder of existingFolders) {
      let shouldKeep = true
      
      // For special folder types (PINNED, SENSITIVE), only keep the first one
      if (folder.folderType && ['PINNED', 'SENSITIVE'].includes(folder.folderType)) {
        if (seenTypes.has(folder.folderType)) {
          shouldKeep = false
        } else {
          seenTypes.add(folder.folderType)
        }
      }
      // For regular folders, check for name duplicates
      else if (folder.name) {
        if (seenNames.has(folder.name)) {
          shouldKeep = false
        } else {
          seenNames.add(folder.name)
        }
      }
      
      if (shouldKeep) {
        foldersToKeep.push(folder)
      } else {
        // Delete duplicate folder
        console.log(`🗑️ BackupService: Removing duplicate folder: ${folder.name} (${folder.folderType || 'CUSTOM'})`)
        await localStorageService.deleteFolder(folder.folderId)
        duplicatesRemoved++
      }
    }
    
    if (duplicatesRemoved > 0) {
      console.log(`✅ BackupService: Cleaned up ${duplicatesRemoved} duplicate folders`)
    }
    
    return foldersToKeep
  }

  /**
   * Merge backup data with existing data
   * Priority: Existing data wins in conflicts (no overwrites)
   */
  async mergeData({ notes, folders, attachments }) {
    const results = {
      notes: { added: 0, skipped: 0, conflicts: [] },
      folders: { added: 0, skipped: 0, conflicts: [] },
      attachments: { added: 0, skipped: 0, conflicts: [] }
    }

    // Clean up any existing duplicate folders first
    console.log('🧹 BackupService: Cleaning up existing duplicate folders...')
    const cleanedFolders = await this.cleanupDuplicateFolders(folders.existing)
    folders.existing = cleanedFolders

    // Merge folders first (notes depend on folders)
    console.log('🔄 BackupService: Merging folders...')
    const existingFolderIds = new Set(folders.existing.map(f => f.folderId))
    const existingFolderTypes = new Set(folders.existing.map(f => f.folderType).filter(Boolean))
    const existingFolderNames = new Set(folders.existing.map(f => f.name).filter(Boolean))
    
    for (const folder of folders.backup) {
      let shouldSkip = false
      let skipReason = ''
      
      // Check for ID conflict
      if (existingFolderIds.has(folder.folderId)) {
        shouldSkip = true
        skipReason = 'duplicate ID'
      }
      // Check for special folder type conflict (PINNED, SENSITIVE should be unique)
      else if (folder.folderType && ['PINNED', 'SENSITIVE'].includes(folder.folderType) && existingFolderTypes.has(folder.folderType)) {
        shouldSkip = true
        skipReason = `duplicate ${folder.folderType} folder type`
      }
      // Check for name conflict (to prevent multiple folders with same name)
      else if (folder.name && existingFolderNames.has(folder.name)) {
        shouldSkip = true
        skipReason = 'duplicate name'
      }
      
      if (!shouldSkip) {
        await localStorageService.saveFolder(folder)
        
        // Update tracking sets
        existingFolderIds.add(folder.folderId)
        if (folder.folderType) existingFolderTypes.add(folder.folderType)
        if (folder.name) existingFolderNames.add(folder.name)
        
        results.folders.added++
        console.log(`📁 BackupService: Added folder: ${folder.name} (${folder.folderType || 'CUSTOM'})`)
      } else {
        results.folders.skipped++
        results.folders.conflicts.push(folder.folderId)
        console.log(`📁 BackupService: Skipped folder: ${folder.name} (${skipReason})`)
      }
    }

    // Merge notes
    console.log('🔄 BackupService: Merging notes...')
    const existingNoteIds = new Set(notes.existing.map(n => n.noteId))
    
    for (const note of notes.backup) {
      if (!existingNoteIds.has(note.noteId)) {
        await localStorageService.saveNote(note)
        results.notes.added++
        console.log(`📝 BackupService: Added note: ${note.title}`)
      } else {
        results.notes.skipped++
        results.notes.conflicts.push(note.noteId)
        console.log(`📝 BackupService: Skipped existing note: ${note.title}`)
      }
    }

    // Merge attachments
    console.log('🔄 BackupService: Merging attachments...')
    const existingAttachmentIds = new Set(attachments.existing.map(a => a.id))
    
    for (const attachment of attachments.backup) {
      if (!existingAttachmentIds.has(attachment.id)) {
        // Save attachment using proper IndexedDB access
        await this.saveAttachmentToLocalStorage(attachment)
        
        results.attachments.added++
        console.log(`📎 BackupService: Added attachment: ${attachment.fileName}`)
      } else {
        results.attachments.skipped++
        results.attachments.conflicts.push(attachment.id)
        console.log(`📎 BackupService: Skipped existing attachment: ${attachment.fileName}`)
      }
    }

    return results
  }



  /**
   * Generate backup filename with timestamp
   */
  generateBackupFilename() {
    const now = new Date()
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5) // Remove milliseconds and colons
    return `NoteSpark_backup_${timestamp}.zip`
  }

  /**
   * Download a blob as a file
   */
  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Save attachment to local storage using proper format
   * @private
   */
  async saveAttachmentToLocalStorage(attachment) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      
      request.onerror = () => reject(request.error)
      
      request.onsuccess = () => {
        const db = request.result
        
        // Check if local_attachments object store exists
        if (!db.objectStoreNames.contains('local_attachments')) {
          console.log('📎 BackupService: No attachments object store found, skipping attachment save')
          resolve(null)
          return
        }
        
        const transaction = db.transaction(['local_attachments'], 'readwrite')
        const store = transaction.objectStore('local_attachments')
        const saveRequest = store.add(attachment)
        
        saveRequest.onsuccess = () => resolve(saveRequest.result)
        saveRequest.onerror = () => reject(saveRequest.error)
      }
    })
  }

  /**
   * Clean up duplicate folders in local storage (standalone function)
   */
  async cleanupLocalFolderDuplicates() {
    try {
      console.log('🧹 BackupService: Starting cleanup of duplicate folders...')
      
      const existingFolders = await localStorageService.getAllFolders()
      const cleanedFolders = await this.cleanupDuplicateFolders(existingFolders)
      
      console.log('✅ BackupService: Folder cleanup completed')
      return {
        originalCount: existingFolders.length,
        cleanedCount: cleanedFolders.length,
        duplicatesRemoved: existingFolders.length - cleanedFolders.length
      }
    } catch (error) {
      console.error('❌ BackupService: Folder cleanup failed:', error)
      throw new Error(`Folder cleanup failed: ${error.message}`)
    }
  }

  /**
   * Validate backup file format
   */
  async validateBackupFile(zipFile) {
    try {
      const zip = new JSZip()
      const zipContent = await zip.loadAsync(zipFile)
      
      // Check for required files
      const hasMetadata = !!zipContent.files['metadata.json']
      const hasNotes = !!zipContent.files['notes.json']
      
      if (!hasMetadata) {
        throw new Error('Invalid backup file: Missing metadata')
      }
      
      // Validate metadata
      if (hasMetadata) {
        const metadataContent = await zipContent.files['metadata.json'].async('string')
        const metadata = JSON.parse(metadataContent)
        
        if (!metadata.appName || metadata.appName !== 'NoteSpark') {
          throw new Error('Invalid backup file: Not a NoteSpark backup')
        }
      }
      
      return true
    } catch (error) {
      console.error('❌ BackupService: Backup validation failed:', error)
      throw new Error(`Invalid backup file: ${error.message}`)
    }
  }
}

const backupService = new BackupService()
export default backupService 